import React, { useState, useEffect, useRef } from 'react';
import { sendEmailQueue } from '../API/emailAPI';
import { getClients } from '../API/clientAPI';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import s3 from './config/aws-config-email-image';
import s3AttachmentUploader from './config/aws-config-email-attachments';
import s3TemplateUploader from './config/aws-config-email-templates'; // S3 configuration for templates
import ImageResize from 'quill-image-resize';

import {
  Button,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  CardHeader,
  TextField,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Alert,
  Stack
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import CampaignIcon from "@mui/icons-material/Campaign";
import BaseWrapper from "../../components/shared/BaseWrapper";

// Register the image resize module
Quill.register('modules/imageResize', ImageResize);

// Registering default fonts with Quill
const Font = Quill.import('formats/font');
Font.whitelist = ['serif', 'sans-serif', 'monospace'];
Quill.register(Font, true);

// Custom font sizes configuration
const Size = Quill.import('formats/size');
Size.whitelist = ['small', 'normal', 'large', 'huge'];
Quill.register(Size, true);

// Specify S3 buckets
const IMAGE_BUCKET = "boaz-email-images";
const ATTACHMENT_BUCKET = "boaz-email-attachments";
const TEMPLATE_BUCKET = "boaz-email-templates"; // Bucket for templates

const SendPromotions = () => {
  const [clients, setClients] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [customEmails, setCustomEmails] = useState('');
  const [customEmailList, setCustomEmailList] = useState([]); // New state for individual custom emails
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [previewBody, setPreviewBody] = useState('');
  const quillRef = useRef(null);
  const imageInputRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false); // State to manage loading
  
  // TEST-ONLY toggle; default false in production
  const [dryRun, setDryRun] = useState(false);
  // const [dryRun, setDryRun] = useState(true); // default to safe for testing

  const [client_filter, setClientFilter] = useState(
    {
      category: 'all',
      service_types: ['general_pest_control', 'termite_control', 'qpms', 'disinfection'],
      location_type: 'all',
    }
  );

  // --- Test controls ---
  // TEST-ONLY cap; uncomment MAX_RECIPIENTS_TEST for debugging so email blast is controlled
  // const MAX_RECIPIENTS_TEST = 5;

  const service_types = [
    "General Pest Control",
    "Termite Control",
    "QPMS",
    "Disinfection"
  ];

  const locationTypeOptions = [
    "Bank",
    "Bar",
    "BPO",
    "Commercial Complex",
    "Commissary",
    "Condo Unit",
    "Condominium",
    "Department Store",
    "Embassy",
    "Food Manufacturing",
    "Government Office",
    "Hospital or Clinic",
    "Hotel",
    "Industrial Plant",
    "Mall",
    "Manufacturing",
    "Office",
    "Parks and Parking",
    "Residential (Apartment)",
    "Residential (Single Unit)",
    "Restaurant or Café",
    "School",
    "Sports Center",
    "Supermarket",
    "Vehicle",
    "Village",
    "Warehouse",
    "Wet Market",
    "Other",
  ];

  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientsError, setClientsError] = useState("");

  useEffect(() => {
    const fetchClients = async () => {
      try {
        setClientsLoading(true);
        setClientsError("");
        const clientsList = await getClients();
        setClients(clientsList);
      } catch (error) {
        console.error("Error fetching clients:", error);
        setClientsError("Failed to load clients.");
      } finally {
        setClientsLoading(false);
      }
    };
    fetchClients();
    loadTemplatesFromS3();
  }, []);

  const retryFetchClients = () => {
    setClientsError("");
    setClientsLoading(true);
    getClients()
      .then(setClients)
      .catch(() => setClientsError("Failed to load clients. Please try again."))
      .finally(() => setClientsLoading(false));
  };

  // Email body height
  const editorSx = {
    "& .ql-container": { minHeight: 260 }, // overall editor box
    "& .ql-editor": { minHeight: 220, fontSize: 16, paddingBottom: "24px" }, // typing area
  };

  const loadTemplatesFromS3 = async () => {
    try {
      const data = await s3TemplateUploader.listObjectsV2({ Bucket: TEMPLATE_BUCKET }).promise();
      const templateKeys = data.Contents.map(item => item.Key);

      const templatePromises = templateKeys.map(key =>
        s3TemplateUploader.getObject({ Bucket: TEMPLATE_BUCKET, Key: key }).promise()
          .then(result => JSON.parse(result.Body.toString('utf-8')))
      );

      const loadedTemplates = await Promise.all(templatePromises);
      setTemplates(loadedTemplates);
    } catch (error) {
      console.error("Error loading templates from S3:", error);
    }
  };

  const saveTemplate = async () => {
    const newTemplate = {
      id: Date.now(),
      name: subject || 'Untitled Template',
      recipients,
      customEmails: customEmailList, // Save customEmailList instead of a string
      subject,
      body,
      attachments,
      uploadedImages,
    };

    try {
      const key = `${newTemplate.id}.json`;
      const params = {
        Bucket: TEMPLATE_BUCKET,
        Key: key,
        Body: JSON.stringify(newTemplate),
        ContentType: 'application/json',
      };
      // Upload template to S3
      await s3TemplateUploader.putObject(params).promise();
      const updatedTemplates = [...templates, newTemplate];
      setTemplates(updatedTemplates);
      alert('Template saved successfully to S3');
    } catch (error) {
      console.error("Error saving template to S3:", error);
      alert('Failed to save the template');
    }
  };

  const loadTemplate = (templateId) => {
    const template = templates.find((tmpl) => tmpl.id === templateId);
    if (template) {
      setRecipients(template.recipients);
      setCustomEmailList(template.customEmails || []); // Load the customEmailList from the template
      setSubject(template.subject);
      setBody(template.body);
      setAttachments(template.attachments);
      setUploadedImages(template.uploadedImages);
      setSelectedTemplate(templateId);
    } else {
      alert('Template not found');
    }
  };

  const deleteTemplate = async (templateId) => {
    try {
      const key = `${templateId}.json`;
      // Delete the template from S3
      await s3TemplateUploader.deleteObject({ Bucket: TEMPLATE_BUCKET, Key: key }).promise();
      const updatedTemplates = templates.filter((tmpl) => tmpl.id !== templateId);
      setTemplates(updatedTemplates);
      alert('Template deleted successfully from S3');
    } catch (error) {
      console.error("Error deleting template from S3:", error);
      alert('Failed to delete the template');
    }
  };

  const handleToggleRecipient = (email) => {
    if (recipients.includes(email)) {
      setRecipients(recipients.filter(recipient => recipient !== email));
    } else {
      setRecipients([...recipients, email]);
    }
  };

  const handleSelectAll = () => {
    setRecipients(clients.map(client => client.email));
  };

  const handleDeselectAll = () => {
    setRecipients([]);
  };

  const handleCustomEmailChange = (event) => {
    setCustomEmails(event.target.value);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAddCustomEmails = () => {
    const customEmailArray = customEmails.split(',')
      .map(email => email.trim())
      .filter(email => validateEmail(email));

    if (customEmailArray.length > 0) {
      setRecipients([...recipients, ...customEmailArray]);
      setCustomEmailList([...customEmailList, ...customEmailArray]); // Update customEmailList state
      setCustomEmails('');
      alert('Custom emails added successfully');
    } else {
      alert('No valid email addresses found.');
    }
  };

  const handleDeleteCustomEmail = (emailToDelete) => {
    setCustomEmailList(customEmailList.filter(email => email !== emailToDelete)); // Remove the email from customEmailList
    setRecipients(recipients.filter(email => email !== emailToDelete)); // Also remove it from the recipients list if present
  };

  const resizeImage = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const reader = new FileReader();

      reader.onload = (e) => {
        img.src = e.target.result;
        img.onload = () => {
          canvas.width = 200;
          canvas.height = 200;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, 200, 200);
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Image resizing failed'));
            }
          }, file.type);
        };
      };

      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const uploadToS3 = async (file, bucket, s3Client, key = null) => {
    const params = {
      Bucket: bucket,
      Key: key || `${Date.now()}-${file.name}`,
      Body: file,
    };

    try {
      const data = await s3Client.upload(params).promise();
      return { location: data.Location, key: params.Key };
    } catch (error) {
      console.error(`Error uploading to S3 bucket ${bucket}:`, error);
      return null;
    }
  };

  const deleteFromS3 = async (bucket, key, s3Client) => {
    const params = {
      Bucket: bucket,
      Key: key,
    };

    try {
      await s3Client.deleteObject(params).promise();
      alert(`File ${key} deleted successfully from S3!`);
    } catch (error) {
      console.error(`Error deleting from S3 bucket ${bucket}:`, error);
      alert(`Failed to delete ${key} from S3.`);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        const { location, key } = await uploadToS3(file, IMAGE_BUCKET, s3);
        if (location) {
          setBody((prevBody) => `${prevBody}<img src="${location}" alt="Embedded Image" style="width:200px;height:200px;" />`);
          setUploadedImages([...uploadedImages, {
            fileName: file.name,
            originalSize: file.size,
            compressedSize: file.size,
            url: location,
            key
          }]);
          // alert(`${file.name} uploaded successfully!`);
        } else {
          alert('Image upload failed');
        }
        if (imageInputRef.current) {
          imageInputRef.current.value = '';
        }
      } catch (error) {
        console.error("Error during image upload:", error);
        alert('An error occurred while uploading the image.');
      }
    }
  };

  const handleAttachmentChange = async (event) => {
    const files = Array.from(event.target.files);
    const uploadedFiles = [];

    for (let file of files) {
      try {
        const { location, key } = await uploadToS3(file, ATTACHMENT_BUCKET, s3AttachmentUploader);
        if (location) {
          uploadedFiles.push({ fileName: file.name, fileUrl: location, fileSize: file.size, key });
          alert(`${file.name} uploaded successfully!`);
        } else {
          alert(`Failed to upload ${file.name}`);
        }
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
      }
    }

    setAttachments([...attachments, ...uploadedFiles]);
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
  };

  const handleDeleteImage = async (key) => {
    await deleteFromS3(IMAGE_BUCKET, key, s3);
    setUploadedImages(uploadedImages.filter((image) => image.key !== key));
  };

  const handleDeleteAttachment = async (key) => {
    await deleteFromS3(ATTACHMENT_BUCKET, key, s3AttachmentUploader);
    setAttachments(attachments.filter((attachment) => attachment.key !== key));
  };

  const handleCategoryChange = (value) => {
    setClientFilter((prev) => ({
      ...prev,
      category: value,
    }))
  }

  const handleServiceTypeChange = (e, formattedOption) => {
    setClientFilter((prev) => {
      const isChecked = e.target.checked; // Extract checked status
      const updatedServiceTypes = isChecked
        ? [...prev.service_types, formattedOption] // Add formattedOption if checked
        : prev.service_types.filter((option) => option !== formattedOption); // Remove if unchecked

      // Return early if there's no change to prevent unnecessary re-renders
      if (updatedServiceTypes === prev.service_types) {
        return prev;
      }

      return {
        ...prev, // Preserve other jobOrder properties
        service_types: updatedServiceTypes,
      };
    });
  };

  const handleLocationTypeChange = (value) => {
    setClientFilter((prev) => ({
      ...prev,
      location_type: value,
    }))
  }

  const handleSendEmail = async () => {

    // Check if there are any attachments
    const hasAttachments = attachments.length > 0;

    // Generate HTML for attachments only if they exist
    const attachmentLinks = hasAttachments
      ? attachments.map(att => {
        const fileName = new URL(att.fileUrl).pathname.split('/').pop(); // Extract file name from URL
        return `<li><a href="${att.fileUrl}" target="_blank">${fileName}</a></li>`;
      }).join('')
      : '';

    // Prepare the HTML content for the email body
    const htmlBody = `    
    <html>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
          }
          .email-container {
            width: 100%;
            max-width: 600px;
            margin: 0 auto;
            padding: 20px;
            
            border: 1px solid #ddd;
          }
          .header, .footer {
            width: 100%;
            text-align: center;
            margin: 0;
            padding: 20px 0;
            
          }
          .content {
            margin: 20px 0;
          }
           .content img {
            max-width: 100%;
            height: auto;
            display: block;
            margin: 0 auto;
          }
        </style>
      </head>
      <body>
        <div class="email-container">
          <!-- Header Section -->
          <div class="header">
            <img src="https://boaz-email-images.s3.ap-southeast-1.amazonaws.com/header_boaz_transparent1.png" alt="Boaz Header" style="max-width: 100%; height: auto;">
          </div>

          <!-- Main Content -->
          <div class="content">
            ${body} <!-- Main email content from React Quill -->
            ${hasAttachments ? `<p><strong>Attachments:</strong></p><ul>${attachmentLinks}</ul>` : ''}

          </div>

          <!-- Footer Section -->
          <a href="mailto:boazpestcontrol@gmail.com" style="text-decoration: none;">
          <div class="footer">
            <img src="https://boaz-email-images.s3.ap-southeast-1.amazonaws.com/footer_boaz_transparent1.png" alt="Boaz Footer" style="max-width: 100%; height: auto;">
            <hr />
            <footer>              
              <p>If you no longer wish to receive these emails, you can <a href="mailto:unsubscribe-boaz@maestrosystemsinc.com">unsubscribe here</a>.</p>
            </footer>
          </div>
          </a>
        </div>
      </body>
    </html>`;

    // --- STRICT extractor: contacts[*].emails[*].email + filters + strong logs
    const extractEmails = (clients) => {
      const norm = (v) => (v ?? "").toString().trim().toLowerCase();

      // Current selections (normalize)
      const selLocation  = norm(client_filter.location_type);
      const selCategory  = norm(client_filter.category);
      const selServices  = new Set((client_filter.service_types || []).map(norm));

      const wantsAllLocation = selLocation === "all";
      const wantsAllCategory = selCategory === "all";
      const wantsAllServices = selServices.size === 4; // your default "all selected"

      // 1) Filter clients
      const filteredClients = (clients || []).filter((c) => {
        const locOk = wantsAllLocation || norm(c.location_type) === selLocation;
        const catOk = wantsAllCategory || norm(c.category) === selCategory;

        // service_types match (any of the selected services) — comment out if you don't want this now
        const cServices = new Set((c.service_types || []).map(norm));
        const svcOk = wantsAllServices ? true : [...selServices].some((s) => cServices.has(s));

        return locOk && catOk && svcOk;
      });

      // Logging: filtered clients summary
      // console.log("[extractEmails] Total clients:", clients?.length || 0);
      // console.log("[extractEmails] Filtered -> location:", client_filter.location_type,
      //             "| category:", client_filter.category,
      //             "| service_types:", client_filter.service_types);
      // console.log("[extractEmails] Filtered clients count:", filteredClients.length);

      // 2) Extract emails strictly from contacts[*].emails[*].email
      const emailSet = new Set();
      const rows = []; // for pretty table

      filteredClients.forEach((client) => {
        const name = client.name || client.client_name || "(no name)";
        (client.contacts || []).forEach((contact) => {
          (contact.emails || []).forEach((em) => {
            const raw = em && em.email;
            const email = raw && raw.toString().trim();
            if (email) {
              const lc = email.toLowerCase();
              if (!emailSet.has(lc)) {
                emailSet.add(lc);
                rows.push({ name, email }); // one row per unique email
              }
            }
          });
        });
      });

      // 3) Logs: exactly who matched
      // console.table(rows);
      // console.log("[extractEmails] Unique emails found:", emailSet.size);

      return { emails: Array.from(emailSet), rows };
    };

    // Build recipients based on current filters
    const { emails: clientEmailList } = extractEmails(clients);

    // Production UX: show a clear message and stop if nothing matched
    if (clientEmailList.length === 0) {
      alert("No recipients matched your current filters. Please adjust Category, Location Type, or Service Type and try again.");
      return;
    }

    // After you build clientEmailList
    // const matchedClients = [];

    // (clients || []).forEach((client) => {
    //   const clientName = client.name || "(no name)";
    //   (client.contacts || []).forEach((contact) => {
    //     (contact.emails || []).forEach((em) => {
    //       if (em && em.email) {
    //         const email = em.email.trim().toLowerCase();
    //         if (clientEmailList.includes(email)) {
    //           matchedClients.push({ name: clientName, email });
    //         }
    //       }
    //     });
    //   });
    // });

    // console.log("Recipients (count):", clientEmailList.length);
    // console.table(matchedClients);

    // TEST-ONLY: cap sends. Keep commented in production.
    // Test mode to use special filter ==> location_type === "Test Email Blast"
    // const isTestMode = client_filter.location_type === "Test Email Blast";

    // Cap in UI (backend also enforces a cap)
    // const finalRecipients = isTestMode
    //   ? clientEmailList.slice(0, MAX_RECIPIENTS_TEST)
    //   : clientEmailList;

    // PRODUCTION: send full filtered set. Comment out when debugging.
    const finalRecipients = clientEmailList;

    // Quick guardrails
    if (!finalRecipients.length || !subject || !htmlBody) {
      console.error("Missing required fields:", { recipients: finalRecipients, subject, body: htmlBody });
      alert("Cannot send email: Missing required fields.");
      return;
    }

    // Optional confirmation so you always see the blast size
    const ok = window.confirm(
      `About to ${dryRun ? "TEST (dry run)" : "SEND"} to ${finalRecipients.length} recipient(s). Continue?`
    );
    if (!ok) return;

    const lambdaPayload = {
      from: "boaz-promotions@maestrosystemsinc.com",
      recipients: finalRecipients,
      subject,
      body: htmlBody,
      // TEST-ONLY flags; leave commented in production:
      // ...(dryRun ? { dryRun: true, maxRecipients: MAX_RECIPIENTS_TEST } : {}),
    };

    // Build a quick lookup of the capped/curr recipients
    const wanted = new Set(finalRecipients.map(e => e.trim().toLowerCase()));

    // Collect matching {name, email} rows from the filtered clients
    const recipientRows = [];
    clients.forEach((client) => {
      const clientName = client.name || client.client_name || "(no name)";
      (client.contacts || []).forEach((contact) => {
        // Support both contact.emails (array of objects/strings) and contact.email (single)
        if (Array.isArray(contact.emails)) {
          contact.emails.forEach((em) => {
            const raw = typeof em === "string" ? em : em?.email;
            const email = raw?.trim();
            if (email && wanted.has(email.toLowerCase())) {
              recipientRows.push({ name: clientName, email });
            }
          });
        }
        if (contact.email) {
          const email = contact.email.trim();
          if (email && wanted.has(email.toLowerCase())) {
            recipientRows.push({ name: clientName, email });
          }
        }
      });
    });

    // Deduplicate rows by email for cleaner output
    const seen = new Set();
    const recipientRowsDedup = recipientRows.filter(({ email }) => {
      const k = email.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });

    // Your existing logs, now with names:
    // console.log("Email Body:", htmlBody);
    // console.table(recipientRowsDedup);      // nice tabular view in devtools
    // console.log("Recipients:", finalRecipients);

    try {
      const response = await sendEmailQueue(lambdaPayload);  // Using the sendEmail function with axios
      alert('Email sent successfully');
    } catch (error) {
      console.error("Error sending the email:", error);
      alert('Failed to send email');
    }
  };


  const previewEmail = () => {
    setPreviewBody(body);
  };

  const modules = {
    toolbar: [
      [{ font: Font.whitelist }],
      [{ size: Size.whitelist }],
      [{ header: '1' }, { header: '2' }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['bold', 'italic', 'underline'],
      [{ align: [] }],
      ['link'],
      ['clean'],
    ],
    imageResize: {},
  };


  return (

    <BaseWrapper
      titleIcon={<CampaignIcon />}
      title="Client Retention"
      subTitle="Send Promotions"
      subTitleRoute="/send-promotions"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      {/* Progress Bar for Loading Clients Data */}
      {clientsLoading ? (
      // ----- Loading screen -----
      <Box role="status" aria-busy="true" sx={{ width: '100%' }}>
        <LinearProgress />
        <Stack spacing={2} alignItems="center" sx={{ py: 6 }}>
          <Typography variant="h6" align="center">
            Please wait while we fetch all client emails…
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            This may take a moment for large lists.
          </Typography>
        </Stack>
      </Box>
      ) : clientsError ? (
        // ----- Error screen -----
        <Stack spacing={2} alignItems="center" sx={{ py: 6 }}>
          <Alert severity="error" variant="outlined" sx={{ width: '100%' }}>
            {clientsError}
          </Alert>
          <Button variant="outlined" onClick={retryFetchClients}>
            Retry
          </Button>
        </Stack>
      ) : (
        // ----- Email form -----
        <Box component="form" noValidate autoComplete="off">
          <Grid container spacing={3}>
            {/* Recipients Field */}
            {/* Uncomment if needed */}
            {/* <Grid item xs={12}>
            <TextField
              fullWidth
              label="Recipients"
              variant="outlined"
              placeholder="Enter recipient emails separated by commas"
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
            />
          </Grid> */}

            {/* Category Field */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="category-label" sx={{ fontSize: "0.875rem" }}>
                  Category
                </InputLabel>
                <Select
                  labelId="category-label"
                  value={client_filter.category}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  label="Category"
                  sx={{
                    fontSize: "0.875rem",
                    "& .MuiSelect-select": { fontSize: "0.875rem" },
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="VVIP">VVIP</MenuItem>
                  <MenuItem value="VIP">VIP</MenuItem>
                  <MenuItem value="Gold">Gold</MenuItem>
                  <MenuItem value="Silver">Silver</MenuItem>
                  <MenuItem value="Bronze">Bronze</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {/* Location Type Field */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="location-type-label" sx={{ fontSize: "0.875rem" }}>
                  Location Type
                </InputLabel>
                  <Select
                    labelId="location-type-label"
                    value={client_filter.location_type}
                    onChange={(e) => handleLocationTypeChange(e.target.value)}
                    label="Location Type"
                    sx={{
                      fontSize: "0.875rem",
                      "& .MuiSelect-select": { fontSize: "0.875rem" },
                    }}
                  >
                    {/* Use custom Location Type = Test Email Blast when testing */}
                    {/* <MenuItem value="Test Email Blast">Test Email Blast</MenuItem> */}
                    <MenuItem value="all">All</MenuItem>
                    {locationTypeOptions.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
              </FormControl>
            </Grid>

            {/* Service Types */}
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  paddingBottom: "0.5rem",
                  fontSize: "0.875rem",
                  color: "gray",
                }}
              >
                Service Type
              </Typography>
              {service_types.map((option) => {
                const formattedOption = option.toLowerCase().replace(/\s+/g, "_");
                return (
                  <FormControlLabel
                    key={formattedOption}
                    control={
                      <Checkbox
                        name={formattedOption}
                        checked={client_filter.service_types.includes(formattedOption)}
                        onChange={(e) => handleServiceTypeChange(e, formattedOption)}
                      />
                    }
                    label={option}
                  />
                );
              })}
            </Grid>



            {/* Subject Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject"
                variant="outlined"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </Grid>

            {/* Email Body */}
            <Grid item xs={12}>
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx = {{ marginBottom: "10px" }}
                  >
                    Email Body
                  </Typography>
                }
              />
              <Button
                variant="contained"
                component="label"
                startIcon={<ImageIcon />}
                sx={{
                  backgroundColor: "#1976d2",
                  color: "white",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#1565c0",
                  },
                  marginBottom: 1,
                }}
              >
                Insert Image
                <input type="file" hidden onChange={handleImageUpload} ref={imageInputRef} />
              </Button>
              <Box sx={editorSx}>
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={body}
                  onChange={setBody}
                  modules={modules}
                  placeholder="Type your email content here…"
                />
              </Box>
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12} sx={{ textAlign: "right" }}>
              <Button
                variant="contained"
                onClick={handleSendEmail}
                sx={{
                  backgroundColor: "#43a047",
                  color: "white",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#388e3c",
                  },
                }}
              >
                Send Email
              </Button>
              {/* TEST-ONLY: Dry run toggle. Keep commented in production. */}
              {/* <FormControlLabel
                control={<Checkbox checked={dryRun} onChange={e => setDryRun(e.target.checked)} />}
                label="Dry run (do not send, just preview batching)"
              /> */}
            </Grid>          
          </Grid>
        </Box>
      )}
    </BaseWrapper>
  );
};

export default SendPromotions;
